/**
 * Settings React Query Hooks
 *
 * Custom hooks for Epic 9: System Settings & Master Data management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  rolesApi,
  permissionsApi,
  departmentsApi,
  functionsApi,
  roleTypesApi,
  settingsCompetenciesApi,
  scoreCriteriaApi,
  agreementTypesApi,
  salaryRangesApi,
  evaluationCyclesApi,
  systemSettingsApi,
  holidaysApi,
  selfServiceFieldsApi,
  employeesDropdownApi,
  auditLogApi,
  type AuditLogQueryParams,
} from '../../lib/settingsApi';
import { niveausApi, type UpdateNiveauInput } from '../../lib/niveausApi';
import type {
  CreateRoleRequest,
  UpdateRoleRequest,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  CreateFunctionRequest,
  UpdateFunctionRequest,
  CreateRoleTypeRequest,
  UpdateRoleTypeRequest,
  CreateCompetencyRequest,
  UpdateCompetencyRequest,
  UpdateScoreCriteriaRequest,
  CreateAgreementTypeRequest,
  UpdateAgreementTypeRequest,
  UpdateSalaryRangeRequest,
  CreateEvaluationCycleRequest,
  UpdateEvaluationCycleRequest,
  UpdateSystemSettingsRequest,
  CreateHolidayRequest,
  UpdateHolidayRequest,
  UpdateSelfServiceFieldsRequest,
} from '@functieloonhuis/shared';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const settingsKeys = {
  all: ['settings'] as const,

  // Roles & Permissions
  roles: () => [...settingsKeys.all, 'roles'] as const,
  role: (id: number) => [...settingsKeys.roles(), id] as const,
  permissions: () => [...settingsKeys.all, 'permissions'] as const,

  // Departments
  departments: () => [...settingsKeys.all, 'departments'] as const,
  department: (id: number) => [...settingsKeys.departments(), id] as const,

  // Functions
  functions: () => [...settingsKeys.all, 'functions'] as const,
  function: (id: number) => [...settingsKeys.functions(), id] as const,

  // Role Types (Epic 1 — Roltypes & niveau-bepaling v1)
  roleTypes: () => [...settingsKeys.all, 'roleTypes'] as const,
  roleType: (id: number) => [...settingsKeys.roleTypes(), id] as const,

  // Competencies
  competencies: () => [...settingsKeys.all, 'competencies'] as const,
  competency: (id: number) => [...settingsKeys.competencies(), id] as const,

  // Score Criteria
  scoreCriteria: (contextType?: string, contextId?: number) =>
    [...settingsKeys.all, 'scoreCriteria', contextType, contextId] as const,
  scoreThresholds: () => [...settingsKeys.all, 'scoreThresholds'] as const,

  // Agreement Types
  agreementTypes: () => [...settingsKeys.all, 'agreementTypes'] as const,
  agreementType: (id: number) => [...settingsKeys.agreementTypes(), id] as const,

  // Salary Ranges
  salaryRanges: () => [...settingsKeys.all, 'salaryRanges'] as const,

  // Niveaus
  niveaus: () => [...settingsKeys.all, 'niveaus'] as const,
  niveau: (id: number) => [...settingsKeys.niveaus(), id] as const,

  // Evaluation Cycles
  evaluationCycles: () => [...settingsKeys.all, 'evaluationCycles'] as const,
  evaluationCycle: (id: number) => [...settingsKeys.evaluationCycles(), id] as const,

  // System Settings
  systemSettings: (category?: string) => [...settingsKeys.all, 'system', category] as const,
  evaluationSettings: () => [...settingsKeys.all, 'evaluationSettings'] as const,
  featureFlags: () => [...settingsKeys.all, 'featureFlags'] as const,
  pauzeVenster: () => [...settingsKeys.all, 'pauzeVenster'] as const,

  // Holidays
  holidays: (year?: number) => [...settingsKeys.all, 'holidays', year] as const,

  // Self-Service
  selfServiceFields: () => [...settingsKeys.all, 'selfServiceFields'] as const,

  // Employees
  employees: () => [...settingsKeys.all, 'employees'] as const,
};

// =============================================================================
// ROLES & PERMISSIONS HOOKS
// =============================================================================

export function useRoles() {
  return useQuery({
    queryKey: settingsKeys.roles(),
    queryFn: rolesApi.list,
  });
}

export function useRole(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.role(id!),
    queryFn: () => rolesApi.get(id!),
    enabled: !!id,
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: settingsKeys.permissions(),
    queryFn: permissionsApi.list,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => rolesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleRequest }) =>
      rolesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.role(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.roles() });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => rolesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roles() });
    },
  });
}

// =============================================================================
// DEPARTMENTS HOOKS
// =============================================================================

export function useDepartments() {
  return useQuery({
    queryKey: settingsKeys.departments(),
    queryFn: departmentsApi.list,
  });
}

export function useDepartment(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.department(id!),
    queryFn: () => departmentsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDepartmentRequest) => departmentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments() });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDepartmentRequest }) =>
      departmentsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.department(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments() });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => departmentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.departments() });
    },
  });
}

// =============================================================================
// FUNCTIONS HOOKS
// =============================================================================

export function useFunctions() {
  return useQuery({
    queryKey: settingsKeys.functions(),
    queryFn: functionsApi.list,
  });
}

export function useFunction(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.function(id!),
    queryFn: () => functionsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateFunction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFunctionRequest) => functionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.functions() });
    },
  });
}

export function useUpdateFunction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFunctionRequest }) =>
      functionsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.function(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.functions() });
    },
  });
}

export function useDeleteFunction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => functionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.functions() });
    },
  });
}

// =============================================================================
// ROLE TYPES HOOKS (Epic 1 — Roltypes & niveau-bepaling v1)
// =============================================================================

export function useRoleTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...settingsKeys.roleTypes(), { includeInactive }] as const,
    queryFn: () => roleTypesApi.list(includeInactive),
  });
}

export function useRoleType(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.roleType(id!),
    queryFn: () => roleTypesApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateRoleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleTypeRequest) => roleTypesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleTypes() });
    },
  });
}

export function useUpdateRoleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleTypeRequest }) =>
      roleTypesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleType(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleTypes() });
    },
  });
}

export function useDeactivateRoleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => roleTypesApi.deactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleType(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleTypes() });
    },
  });
}

export function useReactivateRoleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => roleTypesApi.reactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleType(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.roleTypes() });
    },
  });
}

// =============================================================================
// COMPETENCIES HOOKS
// =============================================================================

export function useSettingsCompetencies() {
  return useQuery({
    queryKey: settingsKeys.competencies(),
    queryFn: settingsCompetenciesApi.list,
  });
}

export function useSettingsCompetency(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.competency(id!),
    queryFn: () => settingsCompetenciesApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateSettingsCompetency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompetencyRequest) => settingsCompetenciesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.competencies() });
    },
  });
}

export function useUpdateSettingsCompetency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCompetencyRequest }) =>
      settingsCompetenciesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.competency(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.competencies() });
    },
  });
}

export function useDeleteSettingsCompetency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => settingsCompetenciesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.competencies() });
    },
  });
}

// =============================================================================
// SCORE CRITERIA HOOKS
// =============================================================================

export function useScoreCriteria(contextType?: string, contextId?: number) {
  return useQuery({
    queryKey: settingsKeys.scoreCriteria(contextType, contextId),
    queryFn: () => scoreCriteriaApi.list(contextType, contextId),
  });
}

export function useUpdateScoreCriteria() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateScoreCriteriaRequest) => scoreCriteriaApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...settingsKeys.all, 'scoreCriteria'] });
      queryClient.invalidateQueries({ queryKey: settingsKeys.scoreThresholds() });
    },
  });
}

export function useScoreThresholds() {
  return useQuery({
    queryKey: settingsKeys.scoreThresholds(),
    queryFn: scoreCriteriaApi.getThresholds,
  });
}

// =============================================================================
// AGREEMENT TYPES HOOKS
// =============================================================================

export function useAgreementTypes() {
  return useQuery({
    queryKey: settingsKeys.agreementTypes(),
    queryFn: agreementTypesApi.list,
  });
}

export function useAgreementType(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.agreementType(id!),
    queryFn: () => agreementTypesApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateAgreementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgreementTypeRequest) => agreementTypesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.agreementTypes() });
    },
  });
}

export function useUpdateAgreementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAgreementTypeRequest }) =>
      agreementTypesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.agreementType(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.agreementTypes() });
    },
  });
}

export function useDeleteAgreementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => agreementTypesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.agreementTypes() });
    },
  });
}

// =============================================================================
// SALARY RANGES HOOKS
// =============================================================================

export function useSalaryRanges() {
  return useQuery({
    queryKey: settingsKeys.salaryRanges(),
    queryFn: salaryRangesApi.list,
  });
}

export function useUpdateSalaryRange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ niveauId, data }: { niveauId: number; data: UpdateSalaryRangeRequest }) =>
      salaryRangesApi.update(niveauId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.salaryRanges() });
    },
  });
}

// =============================================================================
// NIVEAUS HOOKS
// =============================================================================

export function useNiveaus() {
  return useQuery({
    queryKey: settingsKeys.niveaus(),
    queryFn: niveausApi.getAll,
  });
}

export function useNiveau(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.niveau(id!),
    queryFn: () => niveausApi.getById(id!),
    enabled: !!id,
  });
}

export function useUpdateNiveau() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNiveauInput }) =>
      niveausApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.niveau(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.niveaus() });
    },
  });
}

// =============================================================================
// EVALUATION CYCLES HOOKS
// =============================================================================

export function useEvaluationCycles() {
  return useQuery({
    queryKey: settingsKeys.evaluationCycles(),
    queryFn: evaluationCyclesApi.list,
  });
}

export function useEvaluationCycle(id: number | undefined) {
  return useQuery({
    queryKey: settingsKeys.evaluationCycle(id!),
    queryFn: () => evaluationCyclesApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateEvaluationCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEvaluationCycleRequest) => evaluationCyclesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.evaluationCycles() });
    },
  });
}

export function useUpdateEvaluationCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateEvaluationCycleRequest }) =>
      evaluationCyclesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.evaluationCycle(id) });
      queryClient.invalidateQueries({ queryKey: settingsKeys.evaluationCycles() });
    },
  });
}

export function useDeleteEvaluationCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => evaluationCyclesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.evaluationCycles() });
    },
  });
}

export function useActivateEvaluationCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => evaluationCyclesApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.evaluationCycles() });
    },
  });
}

// =============================================================================
// SYSTEM SETTINGS HOOKS
// =============================================================================

export function useSystemSettings(category?: string) {
  return useQuery({
    queryKey: settingsKeys.systemSettings(category),
    queryFn: () => systemSettingsApi.list(category),
  });
}

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSystemSettingsRequest) => systemSettingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
}

export function useEvaluationSettings() {
  return useQuery({
    queryKey: settingsKeys.evaluationSettings(),
    queryFn: systemSettingsApi.getEvaluationSettings,
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: settingsKeys.featureFlags(),
    queryFn: systemSettingsApi.getFeatureFlags,
  });
}

// Epic 1 — Verlof correctie/kort verlof, Story 1.1 Taak 7. Leest via het
// voor iedereen toegankelijke eindpunt (AC-5) — story 1.3 gebruikt deze hook
// in het aanvraagformulier om uren uit een van-tot-tijd te berekenen.
export function usePauzeVenster() {
  return useQuery({
    queryKey: settingsKeys.pauzeVenster(),
    queryFn: systemSettingsApi.getPauzeVenster,
  });
}

// =============================================================================
// HOLIDAYS HOOKS
// =============================================================================

export function useHolidays(year?: number) {
  return useQuery({
    queryKey: settingsKeys.holidays(year),
    queryFn: () => holidaysApi.list(year),
  });
}

export function useCreateHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateHolidayRequest) => holidaysApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...settingsKeys.all, 'holidays'] });
    },
  });
}

export function useUpdateHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHolidayRequest }) =>
      holidaysApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...settingsKeys.all, 'holidays'] });
    },
  });
}

export function useDeleteHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => holidaysApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...settingsKeys.all, 'holidays'] });
    },
  });
}

// =============================================================================
// SELF-SERVICE FIELDS HOOKS
// =============================================================================

export function useSelfServiceFields() {
  return useQuery({
    queryKey: settingsKeys.selfServiceFields(),
    queryFn: selfServiceFieldsApi.list,
  });
}

export function useUpdateSelfServiceFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSelfServiceFieldsRequest) => selfServiceFieldsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.selfServiceFields() });
    },
  });
}

// =============================================================================
// AUDIT LOG HOOKS
// =============================================================================

export function useAuditLog(params?: AuditLogQueryParams) {
  return useQuery({
    queryKey: [...settingsKeys.all, 'auditLog', params],
    queryFn: () => auditLogApi.list(params),
  });
}

// =============================================================================
// EMPLOYEES DROPDOWN HOOKS
// =============================================================================

export function useEmployeesDropdown() {
  return useQuery({
    queryKey: settingsKeys.employees(),
    queryFn: employeesDropdownApi.list,
  });
}
